import {skipToken} from '@reduxjs/toolkit/query';
import {User} from 'components/customer/Auth/store/userSlice';
import {useGetQuickProductsQuery} from 'components/customer/Product/store/productApi';
import {useAppContext} from 'contexts';
import {useCallback, useEffect, useState} from 'react';
import {getProductStyles} from 'service/ProductService';
import {useRoomPreview} from 'components/customer/RoomPlanner/components/helpers/RoomPreview';
import {PartialRoom} from 'shared/types/PartialRoom';

interface GetProductParamsProps {
    room?: PartialRoom;
    userProfile?: Partial<User>;
}

export const getProductParams = (params: GetProductParamsProps) => {
    if (params.room) {
        return {
            toeKickHeight: Number(params.room.toeKickHeight),
            baseDepth: Number(params.room.baseDepth),
            baseHeight: Number(params.room.baseHeight),
            upperDepth: Number(params.room.upperDepth),
            upperHeight: Number(params.room.upperHeight),
            tallDepth: Number(params.room.tallDepth),
            tallHeight: Number(params.room.tallHeight),
        };
    } else if (params.userProfile) {
        return {
            toeKickHeight: Number(params.userProfile.defaultToeKickHeight),
            baseDepth: params.userProfile.job?.room?.sizeDefaults?.base_depth,
            baseHeight: params.userProfile.job?.room?.sizeDefaults?.base_height,
            upperDepth: params.userProfile.job?.room?.sizeDefaults?.upper_depth,
            upperHeight:
                params.userProfile.job?.room?.sizeDefaults?.upper_height,
            tallDepth: params.userProfile.job?.room?.sizeDefaults?.tall_depth,
            tallHeight: params.userProfile.job?.room?.sizeDefaults?.tall_height,
        };
    }

    return skipToken;
};

export const usePreCacheProducts = () => {
    const {userProfile} = useAppContext();
    const isRoomPreview = useRoomPreview();
    const [loading, setLoading] = useState(!isRoomPreview);
    const {isLoading, isUninitialized} = useGetQuickProductsQuery(
        getProductParams({userProfile}),
        {
            skip: isRoomPreview,
        }
    );

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            await getProductStyles();
        } catch (error) {
            // eslint-disable-next-line no-console -- Logging error
            console.error('Error pre-caching products:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isRoomPreview) {
            setLoading(false);
            return;
        }

        if (userProfile && userProfile.isAddProductAvailable) {
            void loadData();
        }
    }, [userProfile?.isAddProductAvailable, isRoomPreview, loadData]);

    if (isRoomPreview) {
        return {loading: false};
    }

    return {
        loading: loading || isLoading || isUninitialized,
    };
};
